import Link from 'next/link';
import { IoCubeOutline } from 'react-icons/io5';
import { TbPlus } from 'react-icons/tb';

import { Anchor, Box, Button, Group, SimpleGrid, Text } from '@mantine/core';

import { Component } from 'models';
import { ComponentHelpers } from 'helpers/ComponentHelpers';

import { DashboardPanel } from 'components/dashboard-panel/DashboardPanel';
import cxPanel from 'components/dashboard-panel/DashboardPanel.module.scss';
import { EmptyMessage } from 'components/empty-message/EmptyMessage';
import { ComponentThumbnail } from 'components/thumbnail/ComponentThumbnail';
import { useComponentMeta } from 'hooks/use-component-meta';
import { useCurrentTeam } from 'hooks/use-current-team';
import { useCurrentTeamCompanies } from 'hooks/use-current-team-companies';
import { useProducts } from 'hooks/use-products';

import { DashboardActionRow, THUMBNAIL_SIZE } from './DashboardActionRow';

const NB_SHOWN_PRODUCTS = 6;

const DashboardProducts = () => {
    const team = useCurrentTeam();
    const { companies } = useCurrentTeamCompanies();

    const { products, totalDocs, isLoading } = useProducts(
        { team: team?.id, showPrivate: true, limit: NB_SHOWN_PRODUCTS, sort: '-updatedAt' },
        Boolean(team) && companies.length > 0,
    );

    if (!companies.length) {
        return null;
    }

    return (
        <DashboardPanel
            icon={<IoCubeOutline />}
            color="orange"
            title={totalDocs ? `Products (${totalDocs})` : 'Products'}
            rightSection={
                <Group gap="md">
                    <Anchor component={Link} href={ComponentHelpers.urls.manage()} fw={500} fz="sm">
                        Manage Products
                    </Anchor>
                    <Button
                        component={Link}
                        href={ComponentHelpers.urls.create({
                            company: companies.length === 1 ? companies[0] : undefined,
                        })}
                        size="xs"
                        leftSection={<TbPlus size={14} strokeWidth={1.25} />}
                    >
                        Add Product
                    </Button>
                </Group>
            }
        >
            {!isLoading && !products.length && (
                <EmptyMessage>
                    <Box c="gray.3">
                        <IoCubeOutline size={50} />
                    </Box>
                    <Text inherit>No products yet</Text>
                    <Text inherit>
                        List your products on your company profile so customers can find and compare them
                    </Text>
                </EmptyMessage>
            )}

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing={0} className={cxPanel.dividedGrid}>
                {products.map((product) => (
                    <ProductItem key={product.id} product={product} />
                ))}
            </SimpleGrid>
        </DashboardPanel>
    );
};

const ProductItem = ({ product }: { product: Component }) => {
    const { uniqueName, typeName, manufacturerName, slug } = useComponentMeta(product);

    return (
        <DashboardActionRow
            href={ComponentHelpers.urls.view(product.id, slug)}
            thumbnail={
                <ComponentThumbnail
                    component={product}
                    showEmpty
                    style={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE, mixBlendMode: 'multiply' }}
                />
            }
            title={uniqueName}
            description={[typeName, manufacturerName].filter(Boolean).join(' • ')}
        />
    );
};

export { DashboardProducts };
